import { DateField, MoonSegmentedControl, TextField } from "@louise/moon-ui";
import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, LayoutAnimation } from "react-native";
import { Calendar, Ruler, Weight } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import { BasicInfoQuestionLayout } from "../components";
import { useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
export function BasicInfoScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("BasicInfo");
  const navigation = useNavigation();
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");
  const [heightUnit, setHeightUnit] = useState<"cm" | "ft">("cm");
  const [weight, setWeight] = useState(state.weight?.toString() || "");
  const [height, setHeight] = useState(state.height?.toString() || "");

  useEffect(() => {
    if (state.height != null) {
      setHeight(state.height.toString());
    }
  }, [state.height]);

  useEffect(() => {
    if (state.weight != null) {
      setWeight(state.weight.toString());
    }
  }, [state.weight]);

  const handleDateChange = useCallback(
    async (iso: string) => {
      if (!iso || iso === state.dateOfBirth) return;
      updateState({ dateOfBirth: iso });
      await updatePatient({ dateOfBirth: iso });
    },
    [state.dateOfBirth, updateState, updatePatient],
  );

  // Save weight to API on blur (always store in kg)
  const handleWeightBlur = useCallback(async () => {
    if (!weight) {
      updateState({ weight: null });
      return;
    }
    const raw = parseInt(weight, 10);
    const weightInKg = weightUnit === "lb" ? Math.round(raw * 0.453592) : raw;
    updateState({ weight: weightInKg });
    await updatePatient({
      bodyData: {
        id: patient?.bodyData?.id,
        weight: weightInKg,
      },
    });
  }, [weight, weightUnit, updateState, updatePatient, patient?.bodyData?.id]);

  // Save height to API on blur (always store in cm)
  const handleHeightBlur = useCallback(async () => {
    if (!height) {
      updateState({ height: null });
      return;
    }
    let heightInCm: number;
    if (heightUnit === "ft") {
      // Parse feet'inches format (e.g. "5'10" or just "510" → 5ft 10in)
      const match = height.match(/^(\d+)'?\s*(\d*)$/);
      if (match) {
        const feet = parseInt(match[1]!, 10);
        const inches = match[2] ? parseInt(match[2], 10) : 0;
        heightInCm = Math.round((feet * 12 + inches) * 2.54);
      } else {
        return;
      }
    } else {
      heightInCm = parseInt(height, 10);
    }
    updateState({ height: heightInCm });
    await updatePatient({
      bodyData: {
        id: patient?.bodyData?.id,
        height: heightInCm,
      },
    });
  }, [height, heightUnit, updateState, updatePatient, patient?.bodyData?.id]);

  const handleSave = () => next();

  return (
    <BasicInfoQuestionLayout
      route="BasicInfo"
      title={t("med_history.basic_info.basics.title")}
      subtitle={t("med_history.basic_info.basics.subtitle")}
      helpText={t("med_history.basic_info.help.basics")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={isPending}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.formContainer}>
        <DateField
          label={t("med_history.basic_info.basics.date_of_birth")}
          labelIcon={Calendar}
          value={state.dateOfBirth ?? ""}
          onChange={handleDateChange}
          disabled={isPending}
        />

        {/* Weight Input */}
        <TextField
          label={t("med_history.basic_info.basics.weight")}
          labelIcon={Weight}
          value={weight}
          onChangeText={(text) => {
            setWeight(text.replace(/[^0-9]/g, ""));
          }}
          onBlur={handleWeightBlur}
          placeholder={t("med_history.basic_info.basics.weight_placeholder")}
          keyboardType="number-pad"
          trailing={
            <MoonSegmentedControl
              tabs={[
                { value: "kg", label: "kg" },
                { value: "lb", label: "lb" },
              ]}
              active={weightUnit}
              onChange={(unit) => {
                LayoutAnimation.configureNext(
                  LayoutAnimation.Presets.easeInEaseOut,
                );
                setWeightUnit(unit);
              }}
              shape="rounded"
              style={{ marginTop: 0, marginRight: 4 }}
            />
          }
        />

        {/* Height Input */}
        <TextField
          label={t("med_history.basic_info.basics.height")}
          labelIcon={Ruler}
          value={height}
          onChangeText={(text) => {
            if (heightUnit === "ft") {
              setHeight(text.replace(/[^0-9']/g, ""));
            } else {
              setHeight(text.replace(/[^0-9]/g, ""));
            }
          }}
          onBlur={handleHeightBlur}
          placeholder={
            heightUnit === "ft"
              ? t("med_history.basic_info.basics.height_placeholder_ft")
              : t("med_history.basic_info.basics.height_placeholder_cm")
          }
          keyboardType={heightUnit === "ft" ? "default" : "number-pad"}
          trailing={
            <MoonSegmentedControl
              tabs={[
                { value: "cm", label: "cm" },
                { value: "ft", label: "ft" },
              ]}
              active={heightUnit}
              onChange={(unit) => {
                LayoutAnimation.configureNext(
                  LayoutAnimation.Presets.easeInEaseOut,
                );
                setHeightUnit(unit);
              }}
              shape="rounded"
              style={{ marginTop: 0, marginRight: 4 }}
            />
          }
        />
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    gap: 24,
  },
});

export default BasicInfoScreen;
